/**
 * PublicFooter - Standalone footer component for all public pages
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CorvanaLogo from '@/components/CorvanaLogo';
import { Mail, MapPin } from 'lucide-react';
import { API_URL } from '@/config/apiConfig';

export default function PublicFooter() {
  const currentYear = new Date().getFullYear();
  const [recentPosts, setRecentPosts] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/blog/posts?limit=6`);
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setRecentPosts((data.posts || data.items || data || []).slice(0, 6));
      } catch { /* non-blocking */ }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <footer className="bg-slate-900 text-white py-12 lg:py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity">
              <CorvanaLogo size="sm" />
              <span className="text-xl font-display font-bold text-white">Corvana</span>
            </Link>
            <p className="text-sm text-slate-400 mb-4 max-w-xs">
              AI-powered business intelligence and data warehouse platform for Australian SMEs.
            </p>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="font-display font-semibold mb-4 text-white">Solutions</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/real-time-business-intelligence" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  Real-Time Business Intelligence
                </Link>
              </li>
              <li>
                <Link to="/ai-business-analytics-australia" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  AI Analytics
                </Link>
              </li>
              <li>
                <Link to="/business-valuation-software" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  Business Valuation
                </Link>
              </li>
              <li>
                <Link to="/compliance-automation-platform" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  Compliance Automation
                </Link>
              </li>
              <li>
                <Link to="/multi-location-analytics" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  Multi-Location
                </Link>
              </li>
              <li>
                <Link to="/answers" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  Answers
                </Link>
              </li>
              <li>
                <Link to="/bi-software-comparison" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  BI Comparison
                </Link>
              </li>
              <li>
                <Link to="/ai-business-intelligence" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  AI Business Intelligence
                </Link>
              </li>
              <li>
                <Link to="/ai-consulting" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  AI Consulting
                </Link>
              </li>
              <li>
                <Link to="/ai-for-australian-business" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  AI for Australian Business
                </Link>
              </li>
              <li>
                <Link to="/ai-for-franchise-restaurants" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  AI for Franchise Restaurants
                </Link>
              </li>
            </ul>
          </div>

          {/* Platform */}
          <div>
            <h4 className="font-display font-semibold mb-4 text-white">Platform</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/industries" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  Industries
                </Link>
              </li>
              <li>
                <Link to="/integrations" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  Integrations
                </Link>
              </li>
              <li>
                <Link to="/data-sources" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  Data Sources
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/developers" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  API
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-display font-semibold mb-4 text-white">Company</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/brief" className="text-sm text-slate-400 hover:text-white transition-colors block" data-testid="footer-brief-link">
                  The Corvana Brief
                </Link>
              </li>
              <li>
                <Link to="/technology" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  Technology
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/partner" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  Partner Program
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/download-capability" className="text-sm text-slate-400 hover:text-white transition-colors block">
                  Capability Statement
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display font-semibold mb-4 text-white">Get in Touch</h4>
            <ul className="space-y-3">
              <li>
                <a href="mailto:hello@corvana.com.au" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  hello@corvana.com.au
                </a>
              </li>
              <li className="text-sm text-slate-400 flex items-center gap-2">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                Sydney, Australia
              </li>
            </ul>
          </div>
        </div>

        {recentPosts.length > 0 && (
          <div className="border-t border-slate-800 mt-10 pt-8" data-testid="footer-latest-blog">
            <h4 className="font-display font-semibold mb-4 text-white">Latest from the blog</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
              {recentPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`footer-blog-link-${p.slug}`}
                  className="text-sm text-slate-400 hover:text-white transition-colors block truncate"
                >
                  {p.title}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-slate-800 mt-10 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-400">
            © {currentYear} Corvana. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm text-slate-400">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <button
              type="button"
              onClick={() => { try { window.dispatchEvent(new Event('cv-open-consent')); } catch { /* noop */ } }}
              className="hover:text-white transition-colors"
              data-testid="footer-cookie-settings"
            >
              Cookie settings
            </button>
            <span className="hidden sm:inline text-slate-600">|</span>
            <span>Made in Australia 🇦🇺</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
